import type { AutoBatchResponse, AutoCandidate } from "@opus-dt/shared-types";

// As MARCAS DE HONESTIDADE da resposta. Um número de simulação sem elas mente por
// omissão: "9 h 20" lido como promessa é diferente de "9 h 20, interpolado, e que só se cumpriu em
// 3 de 5 execuções". Sinalizar estimativa, "não atingiu NO HORIZONTE AVALIADO" (nunca
// "impossível") e a dispersão visível vivem aqui.

export interface AnswerMark {
  kind: "estimate" | "dispersion" | "not-reached" | "quality" | "window";
  label: string;
  /** Frase curta que explica POR QUE a marca existe — não é decoração. */
  detail: string;
}

/** O objetivo mede o tempo até uma quantidade-alvo? Só nesses a janela dos KPIs muda. */
function hasTarget(objective: AutoBatchResponse["objective"]): boolean {
  return objective === "TIME_TO_QUANTITY" || objective === "OP_ON_TIME";
}

export function answerMarks(batch: AutoBatchResponse, winner: AutoCandidate | null): AnswerMark[] {
  const marks: AnswerMark[] = [];
  if (!winner || !hasTarget(batch.objective)) return marks;

  if (winner.targetEstimated) {
    marks.push({
      kind: "estimate",
      label: "Estimativa",
      // Regime sem arestas: uma linha de estágio único não forma fila, então não há evento por
      // peça para observar. Não é um resultado pior — é um resultado de outra natureza.
      detail:
        "Esta linha tem um único estágio, então não há fila entre máquinas: o instante foi " +
        "interpolado da produção acumulada, não observado peça a peça.",
    });
  }

  if (winner.targetReached === false) {
    marks.push({
      kind: "not-reached",
      label: `Não atingida em ${batch.horizonHours} h`,
      detail:
        "A quantidade não foi alcançada dentro do horizonte avaliado. Isso não significa que a " +
        "linha não consiga — significa que não coube no tempo que a simulação percorreu.",
    });
  } else if (winner.targetReachedRatio != null && winner.targetReachedRatio < 1) {
    // A média do instante considera SÓ as execuções que cruzaram, logo é otimista por construção.
    // Esconder a proporção transformaria um resultado instável numa promessa firme.
    const reached = Math.round(winner.targetReachedRatio * 5);
    marks.push({
      kind: "dispersion",
      label: `Atingida em ${reached} de 5 execuções`,
      detail:
        "A simulação repete a rodada algumas vezes com falhas sorteadas. O tempo exibido é a " +
        "média das execuções que chegaram à meta — as demais levaram mais tempo.",
    });
  }

  // A meta é em peças BOAS, mas a linha precisa produzir mais peças brutas para entregá-las. Sem
  // dizer isso, o tempo parece maior do que o ritmo nominal justificaria — e o usuário procura um
  // erro que não existe.
  marks.push({
    kind: "quality",
    label: "Meta em peças boas",
    detail:
      "O tempo considera o refugo esperado: para entregar a quantidade pedida em peças boas, a " +
      "linha produz mais peças do que a meta.",
  });

  marks.push({
    kind: "window",
    label: "Medido até a meta",
    detail:
      "Disponibilidade, desempenho e qualidade desta rodada foram medidos no tempo até a meta, " +
      "não no horizonte inteiro — comparar com uma rodada de janela fixa exige atenção.",
  });

  return marks;
}
